/**
 * YUGEN Collector Gallery
 * Horizontal editorial carousel with optional thumbnail strip.
 * Vanilla JS only. Multiple instances supported.
 */
(function () {
  'use strict';

  if (customElements.get('yugen-collector-gallery')) return;

  class YugenCollectorGallery extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.busy = false;
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-ycg-track]');
      this.cards = Array.from(this.querySelectorAll('[data-ycg-card]'));
      this.prevBtn = this.querySelector('[data-ycg-prev]');
      this.nextBtn = this.querySelector('[data-ycg-next]');
      this.currentEl = this.querySelector('[data-ycg-current]');
      this.totalEl = this.querySelector('[data-ycg-total]');
      this.barFill = this.querySelector('[data-ycg-bar]');
      this.thumbs = Array.from(this.querySelectorAll('[data-ycg-thumb]'));
      this.videos = Array.from(this.querySelectorAll('.ycg__video'));

      if (!this.track || this.cards.length === 0) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);

      if (this.totalEl) {
        this.totalEl.textContent = String(this.cards.length).padStart(2, '0');
      }

      this.setupVideos();
      this.setActive(0, { instant: true });
      this.setAttribute('tabindex', '0');
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.thumbs) {
        this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
      }
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
    }

    setupVideos() {
      if (this.videos.length === 0) return;

      this.videos.forEach((video) => {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
      });

      if (!('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            var video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
              var playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(function () {});
              }
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0.35, 0.55, 0.75] }
      );

      this.videos.forEach((video) => this.videoObserver.observe(video));
    }

    onPrev() {
      this.goTo(this.index - 1);
    }

    onNext() {
      this.goTo(this.index + 1);
    }

    onThumbClick(event) {
      var i = parseInt(event.currentTarget.getAttribute('data-ycg-thumb'), 10);
      if (!isNaN(i)) this.goTo(i);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      }
    }

    onScroll() {
      if (this.busy) return;
      if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        var nearest = this.nearestIndex();
        if (nearest !== this.index) this.setActive(nearest, { syncScroll: false });
      }, 80);
    }

    onResize() {
      this.goTo(this.index, true);
    }

    nearestIndex() {
      if (!this.track || this.cards.length === 0) return 0;

      var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      // When fully (or nearly) scrolled to the end, force the last card active.
      if (maxScroll > 0 && this.track.scrollLeft >= maxScroll - 4) {
        return this.cards.length - 1;
      }

      var styles = window.getComputedStyle(this.track);
      var pad =
        parseFloat(styles.scrollPaddingInlineStart) ||
        parseFloat(styles.paddingInlineStart) ||
        parseFloat(styles.paddingLeft) ||
        0;
      var left = this.track.scrollLeft + pad;
      var best = 0;
      var bestDist = Infinity;
      this.cards.forEach((card, i) => {
        var dist = Math.abs(card.offsetLeft - left);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    cardScrollLeft(i) {
      if (!this.track || !this.cards[i]) return 0;
      var styles = window.getComputedStyle(this.track);
      var pad =
        parseFloat(styles.scrollPaddingInlineStart) ||
        parseFloat(styles.paddingInlineStart) ||
        parseFloat(styles.paddingLeft) ||
        0;
      var target = this.cards[i].offsetLeft - pad;
      var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (i === this.cards.length - 1) {
        return maxScroll;
      }
      return Math.max(0, Math.min(target, maxScroll));
    }

    goTo(next, instant) {
      var max = this.cards.length - 1;
      var i = Math.max(0, Math.min(next, max));
      this.setActive(i, { instant: !!instant, syncScroll: true });
    }

    setActive(i, options) {
      options = options || {};
      var syncScroll = options.syncScroll !== false;
      var instant = !!options.instant;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.index = i;

      this.cards.forEach((card, idx) => {
        card.classList.toggle('is-active', idx === i);
      });

      this.thumbs.forEach((thumb, idx) => {
        var active = idx === i;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (this.currentEl) {
        this.currentEl.textContent = String(i + 1).padStart(2, '0');
      }

      if (this.barFill) {
        var pct = ((i + 1) / this.cards.length) * 100;
        this.barFill.style.width = pct + '%';
      }

      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === this.cards.length - 1;

      if (syncScroll && this.track && this.cards[i]) {
        this.busy = true;
        var left = this.cardScrollLeft(i);
        if (instant || reduce) {
          this.track.scrollLeft = left;
          this.busy = false;
        } else if (typeof this.track.scrollTo === 'function') {
          this.track.scrollTo({ left: left, behavior: 'smooth' });
          window.setTimeout(() => {
            this.busy = false;
          }, 420);
        } else {
          this.track.scrollLeft = left;
          this.busy = false;
        }
      }

      var activeThumb = this.thumbs[i];
      if (activeThumb && activeThumb.scrollIntoView) {
        activeThumb.scrollIntoView({
          behavior: reduce ? 'auto' : 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }
  }

  customElements.define('yugen-collector-gallery', YugenCollectorGallery);
})();
